import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PROFILE_ROUTES, ROUTES } from 'configs';
import { SubscriptionFrequency, UserSubscription } from 'types';
import { formatMoney } from 'utils/money';
import type { BillingAttentionReason } from 'utils/subscriptionState';

import { IconAttention, IconCheck, TrialBannerWarningLottie } from './icons';
import {
  Badge,
  FrequencyPill,
  StatusBanner,
  StatusBannerBody,
  StatusBannerCta,
  StatusBannerMain,
  StatusBannerTextCol,
  StatusBannerTitle,
  StatusCheckWrap,
  TrialBanner,
  TrialBannerContent,
  TrialBannerText,
  TrialBannerTitle,
} from './styled';

type SubscriptionStatusBannerProps = {
  subscription: UserSubscription;
  planName: string;
  subscriptionEndDate: string;
  isPlanTrial: boolean;
  isTrial: boolean;
  isFullyCancelled: boolean;
  isLegacyCancelled: boolean;
  isPaidActive: boolean;
  isStripeNonRenewingActive: boolean;
  billingAttentionReason: BillingAttentionReason | null;
  billingAttentionActionUrl?: string;
};

const SuccessBanner = ({
  planName,
  body,
  frequency,
}: {
  planName: string;
  body: React.ReactNode;
  frequency: SubscriptionFrequency | string | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <StatusBanner $variant="success">
      <StatusBannerMain>
        <StatusCheckWrap>
          <IconCheck />
        </StatusCheckWrap>
        <StatusBannerTextCol>
          <StatusBannerTitle as="div" style={{ display: 'block' }}>
            {t('subscriptions.profile.statusBanner.activeLabel', {
              plan: planName,
            })}
          </StatusBannerTitle>
          <StatusBannerBody
            as="div"
            style={{ marginLeft: 0, display: 'block', marginTop: 4 }}
          >
            {body}
          </StatusBannerBody>
        </StatusBannerTextCol>
      </StatusBannerMain>
      <FrequencyPill>
        {frequency === SubscriptionFrequency.YEAR
          ? t('subscriptions.profile.statusBanner.frequencyAnnual')
          : t('subscriptions.profile.statusBanner.frequencyMonthly')}
      </FrequencyPill>
    </StatusBanner>
  );
};

const MutedCancelledBanner = ({
  title,
  body,
}: {
  title: string;
  body: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <StatusBanner $variant="muted">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: 4 }}>
          <StatusBannerTitle>{title}</StatusBannerTitle>
        </div>
        <StatusBannerBody as="div" style={{ marginLeft: 0, display: 'block' }}>
          {body}
        </StatusBannerBody>
      </div>
      <Badge $tone="slate">
        {t('subscriptions.profile.plan.badgeCancelled')}
      </Badge>
    </StatusBanner>
  );
};

const SubscriptionStatusBanner = ({
  subscription,
  planName,
  subscriptionEndDate,
  isPlanTrial,
  isTrial,
  isFullyCancelled,
  isLegacyCancelled,
  isPaidActive,
  isStripeNonRenewingActive,
  billingAttentionReason,
  billingAttentionActionUrl,
}: SubscriptionStatusBannerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const periodForBanner =
    subscription.frequency === SubscriptionFrequency.YEAR
      ? t('changePlan.perYear')
      : t('changePlan.perMonth');
  const currency = subscription.subscriptionPlan?.currency || 'USD';
  const renewalAmt = subscription.proratedValue;

  if (isPlanTrial) {
    return (
      <SuccessBanner
        planName={planName}
        frequency={subscription.frequency}
        body={t('subscriptions.profile.statusBanner.trialChargeRich', {
          trialEndDate: subscriptionEndDate,
          planPrice: `${formatMoney(currency, renewalAmt)}${periodForBanner}`,
        })}
      />
    );
  }

  if (isTrial) {
    return (
      <TrialBanner>
        <TrialBannerWarningLottie />
        <TrialBannerContent>
          <TrialBannerTitle>
            {t('subscriptions.profile.statusBanner.trialTitle')}
          </TrialBannerTitle>
          <TrialBannerText>
            {t('subscriptions.profile.statusBanner.trialBody', {
              date: subscriptionEndDate,
            })}
          </TrialBannerText>
        </TrialBannerContent>
      </TrialBanner>
    );
  }

  if (billingAttentionReason) {
    const openBilling = () => {
      if (billingAttentionActionUrl) {
        window.open(billingAttentionActionUrl, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <StatusBanner $variant="attention">
        <StatusBannerMain>
          <StatusCheckWrap $tone="attention">
            <IconAttention />
          </StatusCheckWrap>
          <StatusBannerTextCol>
            <StatusBannerTitle
              as="div"
              style={{ display: 'block', color: '#9b1c1c' }}
            >
              {t(
                `subscriptions.billingAttention.${billingAttentionReason}.title`,
              )}
            </StatusBannerTitle>
            <StatusBannerBody
              as="div"
              style={{
                marginLeft: 0,
                display: 'block',
                marginTop: 4,
                color: '#9b1c1c',
              }}
            >
              {t(
                `subscriptions.billingAttention.${billingAttentionReason}.body`,
              )}
            </StatusBannerBody>
          </StatusBannerTextCol>
        </StatusBannerMain>
        {billingAttentionActionUrl ? (
          <StatusBannerCta type="button" onClick={openBilling}>
            {t(`subscriptions.billingAttention.${billingAttentionReason}.cta`)}
          </StatusBannerCta>
        ) : (
          <StatusBannerCta
            type="button"
            onClick={() =>
              navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`)
            }
          >
            {t('subscriptions.billingAttention.viewSubscription')}
          </StatusBannerCta>
        )}
      </StatusBanner>
    );
  }

  if (isFullyCancelled) {
    return (
      <MutedCancelledBanner
        title={t('subscriptions.profile.statusBanner.fullyCancelledTitle')}
        body={t('subscriptions.profile.statusBanner.fullyCancelledRich', {
          date: subscriptionEndDate,
        })}
      />
    );
  }

  if (isPaidActive && isStripeNonRenewingActive) {
    return (
      <SuccessBanner
        planName={planName}
        frequency={subscription.frequency}
        body={t('subscriptions.profile.statusBanner.nonRenewalRich', {
          date: subscriptionEndDate,
          price: formatMoney(currency, renewalAmt),
          period: periodForBanner,
        })}
      />
    );
  }

  if (isPaidActive) {
    return (
      <SuccessBanner
        planName={planName}
        frequency={subscription.frequency}
        body={t('subscriptions.profile.statusBanner.renewalRich', {
          date: subscriptionEndDate,
          price: formatMoney(currency, renewalAmt),
          period: periodForBanner,
        })}
      />
    );
  }

  if (isLegacyCancelled) {
    return (
      <MutedCancelledBanner
        title={t('subscriptions.cancelledTitle')}
        body={t('subscriptions.profile.labels.infoCancelled')}
      />
    );
  }

  return null;
};

export default SubscriptionStatusBanner;
